import React, { useState, ReactNode } from 'react'

// @ts-expect-error - Import needs esModuleInterop flag
import { useTranslation } from '@contexts/translation'
// @ts-expect-error - Import needs esModuleInterop flag
import useTableStyles from '@styles/table/table'
import { useTheme } from '@mui/material/styles'

import {
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TableFooter,
  Typography,
  Chip,
  TableSortLabel
} from '@mui/material'

import PlaceHolder from '@components/common/PlaceHolder'
import numberWithCommas from '@utils/numberWithCommas'

import ActionsPopover from '@components/common/ActionsPopover'
import { setItemsSort } from '@utils/sortTables'
import getTotal from '@utils/getTotal'
import FixedFooter from '@components/FixedFooter'
import CommonPagination from './Pagination'
// @ts-expect-error - Import needs esModuleInterop flag
import { useAuth } from '@contexts/auth'
// @ts-expect-error - Import needs esModuleInterop flag
import { useRouter } from 'next/router'

interface LeaseRentData {
  rent_total: number
  [key: string]: any
}

interface Lease {
  id: string | number
  display: string
  unique_identifier?: string
  start_date: string
  end_date: string
  status:
    | 'draft'
    | 'prospect'
    | 'not_started'
    | 'active'
    | 'expired'
    | 'canceled'
    | 'archived'
  rent_data?: LeaseRentData
  balance: number
  provision?: number
  operation?: string | number
  [key: string]: any
}

interface Operation {
  id: string | number
  display: string
  [key: string]: any
}

interface DetailModal {
  open: boolean
  id: string | number
}

interface LeasesTableProps {
  /** Function to set the detail modal state */
  setDetailModal: (modal: DetailModal) => void
  /** Whether the table is in a detail modal */
  isInDetailModal?: boolean
  /** Tenant operations data */
  tenantOperations?: Operation[]
  /** Whether the membership is read-only */
  membershipIsReadOnly?: boolean
  /** Whether this is an article lot table */
  isArticleLotTable?: boolean
  /** Whether the data is loading */
  isLoading?: boolean
  /** Currency symbol */
  currency?: string
  /** Leases data array */
  leases?: Lease[]
  /** Function to set leases data */
  setLeases?: (leases: Lease[]) => void
  /** Query parameters */
  queries?: any
  /** Function to set query parameters */
  setQueries?: (queries: any) => void
  /** Current page number */
  page?: number
  /** Function to set current page */
  setPage?: (page: number) => void
  /** Number of rows per page */
  rowsPerPage?: number
  /** Function to set rows per page */
  setRowsPerPage?: (rowsPerPage: number) => void
  /** Total count of items */
  count?: number
  /** Function to generate actions for each lease */
  ACTIONS?: (id: string | number, lease: Lease) => any[]
}

interface ChipStatusColors {
  label: string
  color: string
  backgroundColor: string
}

interface FooterData {
  [key: number]: {
    name: string
    align?: 'left' | 'right' | 'center'
  }
}

declare module '@mui/material/styles' {
  interface TypeText {
    empty: string
  }
}

/**
 * LeasesTable component
 * A table component for displaying lease data
 */
export default function LeasesTable(props: LeasesTableProps): JSX.Element {
  const {
    setDetailModal,
    isInDetailModal = false,
    tenantOperations = [],
    membershipIsReadOnly = false,
    isArticleLotTable = false,
    isLoading = false,
    currency = '€',
    leases = [],
    setLeases = () => {},
    queries = {},
    setQueries = () => {},
    page = 0,
    setPage = () => {},
    rowsPerPage = 10,
    setRowsPerPage = () => {},
    count = 0,
    ACTIONS = () => []
  } = props

  const { user } = useAuth()
  const { dictionary, t } = useTranslation()
  const theme = useTheme()
  const classesTable = useTableStyles()
  const router = useRouter()
  const [sort, setSort] = useState<'asc' | 'desc'>('asc')
  const [sortBy, setSortBy] = useState<string | undefined>()

  const currentURL = router.asPath

  const isReadOnly = isInDetailModal || membershipIsReadOnly

  const leaseChipStatusColors = (status: Lease['status']): ChipStatusColors => {
    switch (status) {
      case 'draft':
        return {
          label: dictionary.draft?.toString() || 'Draft',
          color: 'highlight.red',
          backgroundColor: 'highlight.lightRed'
        }
      case 'prospect':
        return {
          label: dictionary.prospect?.toString() || 'Prospect',
          color: 'highlight.blue',
          backgroundColor: 'highlight.lightBlue'
        }
      case 'not_started':
        return {
          label: dictionary.not_started?.toString() || 'Not Started',
          color: 'primary.main',
          backgroundColor: 'highlight.lightPurple'
        }
      case 'active':
        return {
          label: dictionary.active_m?.toString() || 'Active',
          color: 'highlight.green',
          backgroundColor: 'highlight.lightGreen'
        }
      case 'expired':
        return {
          label: dictionary.expired?.toString() || 'Expired',
          color: 'dark.main',
          backgroundColor: 'highlight.lightBlack'
        }
      case 'canceled':
        return {
          label: dictionary.canceled?.toString() || 'Canceled',
          color: 'highlight.red',
          backgroundColor: 'highlight.lightRed'
        }
      case 'archived':
        return {
          label: dictionary.archived?.toString() || 'Archived',
          color: 'highlight.gold',
          backgroundColor: 'highlight.lightGold'
        }
      default:
        return {
          label: '',
          color: '',
          backgroundColor: ''
        }
    }
  }

  const isTenantPage =
    user?.role === 'tenant' || currentURL.includes('tenant/preview')

  const isLotsPage =
    typeof window !== 'undefined' && window.location.href.includes('lots/lots')

  const totalCol = isTenantPage || isLotsPage ? 6 : 7

  const FooterObjData: FooterData = {
    0: { name: 'Total', align: 'left' },
    4: {
      name: numberWithCommas(
        getTotal(leases, 'rent_data.rent_total'),
        2,
        currency
      )
    },
    5: {
      name: numberWithCommas(getTotal(leases, 'balance'), 2, currency)
    }
  }

  return (
    <Grid item xs={12}>
      <TableContainer className={classesTable.container}>
        <Table>
          {leases?.length > 0 && (
            <TableHead>
              <TableRow hover={false}>
                {!tenantOperations.length ? (
                  <TableCell className={classesTable.headerCell}>
                    <TableSortLabel
                      active={sortBy === 'display'}
                      direction={sort}
                      onClick={() =>
                        setItemsSort(
                          'display',
                          setSortBy,
                          sort,
                          setSort,
                          setLeases
                        )
                      }
                    >
                      {dictionary.tenant?.toString() || 'Tenant'}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell className={classesTable.headerCell}>
                    <TableSortLabel
                      active={sortBy === 'operation'}
                      direction={sort}
                      onClick={() =>
                        setItemsSort(
                          'operation',
                          setSortBy,
                          sort,
                          setSort,
                          setLeases
                        )
                      }
                    >
                      {dictionary.project?.toString() || 'Project'}
                    </TableSortLabel>
                  </TableCell>
                )}

                <TableCell className={classesTable.headerCell}>
                  <TableSortLabel
                    active={sortBy === 'start_date'}
                    direction={sort}
                    onClick={() =>
                      setItemsSort(
                        'start_date',
                        setSortBy,
                        sort,
                        setSort,
                        setLeases
                      )
                    }
                  >
                    {dictionary.start_date?.toString() || 'Start Date'}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classesTable.headerCell}>
                  <TableSortLabel
                    active={sortBy === 'end_date'}
                    direction={sort}
                    onClick={() =>
                      setItemsSort(
                        'end_date',
                        setSortBy,
                        sort,
                        setSort,
                        setLeases
                      )
                    }
                  >
                    {dictionary.end_date?.toString() || 'End Date'}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classesTable.headerCell} align="center">
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sort}
                    onClick={() =>
                      setItemsSort(
                        'status',
                        setSortBy,
                        sort,
                        setSort,
                        setLeases
                      )
                    }
                  >
                    {dictionary.status?.toString() || 'Status'}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classesTable.headerCell} align="right">
                  <TableSortLabel
                    active={sortBy === 'rent'}
                    direction={sort}
                    onClick={() =>
                      setItemsSort('rent', setSortBy, sort, setSort, setLeases)
                    }
                  >
                    {(isArticleLotTable
                      ? (dictionary.lot?.toString() || 'Lot') + ' '
                      : '') + (dictionary.rent?.toString() || 'Rent')}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classesTable.headerCell} align="right">
                  <TableSortLabel
                    active={sortBy === 'balance'}
                    direction={sort}
                    onClick={() =>
                      setItemsSort(
                        'balance',
                        setSortBy,
                        sort,
                        setSort,
                        setLeases
                      )
                    }
                  >
                    {dictionary.balance?.toString() || 'Balance'}
                  </TableSortLabel>
                </TableCell>
                {!isReadOnly && <TableCell />}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {isLoading && (
              <TableRow className={classesTable.row}>
                <TableCell align="center" colSpan={'100%' as any}>
                  <CircularProgress color="secondary" />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !leases.length && (
              <TableRow hover={false} className={classesTable.row}>
                <TableCell align="center" colSpan={'100%' as any}>
                  <PlaceHolder
                    src="/Placeholders/Keysy/Baux.png"
                    wordingBordered={t('projects_leases_lease_no_rental')}
                  />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              leases.length > 0 &&
              leases.map((lease, idx) => (
                <TableRow
                  key={idx}
                  className={classesTable.row}
                  sx={{ cursor: isTenantPage ? 'default' : 'pointer' }}
                  onClick={
                    isTenantPage
                      ? undefined
                      : () => setDetailModal({ open: true, id: lease.id })
                  }
                >
                  {!tenantOperations.length ? (
                    <TableCell>
                      {lease.display}
                      <Typography
                        sx={{
                          // @ts-expect-error - theme.palette.text.empty is defined in the theme
                          color: theme.palette.text.empty,
                          fontSize: 10
                        }}
                        data-testid="Typography-1b5690ab-b427-4cc1-a897-69a579c78950"
                      >
                        {lease.unique_identifier}
                      </Typography>
                    </TableCell>
                  ) : (
                    <TableCell>
                      {
                        tenantOperations.find(
                          operation => operation.id === lease.operation
                        )?.display
                      }
                    </TableCell>
                  )}

                  <TableCell>{lease.start_date}</TableCell>
                  <TableCell>{lease.end_date}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={leaseChipStatusColors(lease.status).label}
                      sx={{
                        height: 26,
                        fontSize: 12,
                        fontWeight: 500,
                        color: leaseChipStatusColors(lease.status).color,
                        backgroundColor: leaseChipStatusColors(lease.status)
                          .backgroundColor,
                        borderRadius: '8px'
                      }}
                      data-testid="Chip-ca22f352-a97e-4559-86cd-6b85068317ba"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(
                      lease?.rent_data?.rent_total,
                      2,
                      currency
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(
                      isArticleLotTable ? lease.provision || 0 : lease.balance,
                      2,
                      currency
                    )}
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell className={classesTable.rowActions}>
                      <ActionsPopover actions={ACTIONS(lease.id, lease)} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
          {!isLoading && leases?.length > 0 && (
            <TableFooter className={classesTable.footer}>
              <FixedFooter
                data={FooterObjData}
                totalCol={totalCol}
                // @ts-expect-error - classFooter is required by FixedFooter
                classFooter={classesTable.footer}
              />
              <CommonPagination
                queries={queries}
                // @ts-expect-error - setQueries prop on CommonPagination has a different signature
                setQueries={setQueries}
                count={count}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
              />
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Grid>
  )
}
